import React, { useEffect, useRef, useState } from 'react';
import PhaserView, { PhaserRef } from '../Phaser';
import { EventManager } from '../Phaser/engine/events/EventManager';
import { message } from 'antd';
import { TLesson } from '../../models/courses/lesson';
import { TSprite } from '../../models/objects/sprite';
import { TObstacle, TScene } from '../../models/objects/objects';
import { IDEPhaserConfig } from './config';
import { getDefaultConfigPhaser } from '../Phaser/types/config';
import { TSound } from '../../models/objects/media';
import { UnifiedIDE } from '../IDE/ide';
import { getIDEConfigDefault, LanguageId } from '../IDE/ide/core/types';
import ResponsiveIDEPhaserLayout from './ResponsiveIDEPhaserLayout';
import { buildLessonAssetsFromPhaserConfig } from '../../utils/phaserLessonAssets';

interface IDEPhaserTeacherProps {
    lesson: TLesson | undefined;
    language: LanguageId;
    onCodeChange?: (code: string) => void;
}

const IDEPhaserTeacher: React.FC<IDEPhaserTeacherProps> = ({ lesson, language, onCodeChange }) => {
    const phaserRef = useRef<PhaserRef>(null);
    const eventManagerRef = useRef<EventManager | null>(null);

    const [dbSprites, setDbSprites] = useState<TSprite[]>([]);
    const [dbScenes, setDbScenes] = useState<TScene[]>([]);
    const [dbObstacles, setDbObstacles] = useState<TObstacle[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [sounds, setSounds] = useState<TSound[]>([]);

    const [finalConfig, setFinalConfig] = useState<IDEPhaserConfig | null>(null);
    const [latestCode, setLatestCode] = useState<string>('');

    const onWin = () => {
        message.success('🎉 Bravo! Vous avez gagné!', 5);
    };

    useEffect(() => {
        if (!lesson) return;
        setLoading(true);
        const evManager = new EventManager();
        eventManagerRef.current = evManager;

        let config = lesson.config?.config as IDEPhaserConfig;
        if (!config) {
            config = {
                configMirror: getIDEConfigDefault(),
                configPhaser: getDefaultConfigPhaser(),
            };
        }
        const lessonAssets = buildLessonAssetsFromPhaserConfig(config.configPhaser);
        setDbSprites(lessonAssets.sprites);
        setDbScenes(lessonAssets.scenes);
        setDbObstacles(lessonAssets.obstacles);
        setSounds(lessonAssets.sounds);

        // Use teacherCode instead of initialCode, fallback to initialCode if teacherCode not available
        const teacherCode = config.configMirror?.teacherCode || config.configMirror?.initialCode || '';
        setLatestCode(teacherCode);
        setFinalConfig({
            configMirror: {
                ...config.configMirror,
            },
            configPhaser: {
                ...config.configPhaser,
            },
        });
        setLoading(false);
        // eslint-disable-next-line
    }, [lesson]);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
            }}
        >
            {lesson?.statement && (
                <div
                    style={{
                        fontSize: '1.1em',
                        color: '#333',
                        fontWeight: 600,
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {lesson.statement}
                </div>
            )}

            {finalConfig && !loading && (
                <div style={{ flex: 1 }}>
                    <ResponsiveIDEPhaserLayout
                        style={{ height: '500px', minHeight: 0 }}
                        ideComponent={
                            <UnifiedIDE
                                language={language}
                                initialCode={finalConfig.configMirror?.teacherCode || finalConfig.configMirror?.initialCode || ''}
                                projectMode={false}
                                adminMode={false}
                                sprites={dbSprites}
                                scenesConfig={dbScenes}
                                obstacles={dbObstacles}
                                sounds={sounds}
                                // Propager la configuration d'aide définie côté admin (leçon)
                                helpConfig={finalConfig.configMirror?.helpConfig}
                                helpGrouped={finalConfig.configMirror?.helpGrouped}
                                // Ignorer le stockage local sur l'UI enseignant pour respecter la config serveur
                                helpStorageKey={null}
                                onStop={async () => {
                                    await phaserRef.current?.handleResetConfig();
                                }}
                                onRestoreInitial={() => {
                                    const teacherCode = finalConfig.configMirror?.teacherCode || finalConfig.configMirror?.initialCode || '';
                                    setLatestCode(teacherCode);
                                }}
                                onCodeChange={(code: string) => {
                                    setLatestCode(code);
                                    onCodeChange?.(code);
                                }}
                            />
                        }
                        phaserComponent={
                            <PhaserView
                                projectMode={false}
                                adminMode={false}
                                ref={phaserRef}
                                eventManager={eventManagerRef.current}
                                config={finalConfig.configPhaser}
                                isRunning={true}
                                onWin={onWin}
                                sprites={dbSprites}
                                scenesConfig={dbScenes}
                                obstacles={dbObstacles}
                                textsConfig={finalConfig.configPhaser.texts}
                                sounds={sounds}
                            />
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default IDEPhaserTeacher;
